import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ProjectList.module.scss';
import { inAppClick } from '../../utils/linkClick';
import {IconTemplate, IconTrash} from '@tabler/icons-react';
import ProjectCardView from './ProjectCardView';
import { ProjectType } from '../../api/model';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import ProjectScoreCell from './ProjectScoreCell';
import ProjectTrendCell from './ProjectTrendCell';
import { KPI } from '../ReportMetrics/intelligence';
import type { PortfolioRow } from '../ReportMetrics/portfolio';

export interface ProjectListProject {
  id: string;
  title: string;
  description: string;
  logoUrl: string;
  type: ProjectType;
  method?: 'AGILE' | 'CLASSIC' | 'HYBRID';
  status: 'Completed' | 'Delayed' | 'In Progress' | 'Place holder';
  teamMembers: Array<{
    id: string;
    name: string;
    avatarUrl: string;
  }>;
  lastEdit: {
    date: string;
    editor: string;
  };
  progress: number;
  startDate?: string;
  endDate?: string;
  milestone?: string;
  /**
   * The project's KPI engine row (spec §17's Dashboard columns), absent when
   * the engine has never run for it or the portfolio has not loaded yet. Every
   * column built from it renders a dash in that case rather than a zero.
   */
  intelligence?: PortfolioRow;
}

/**
 * The health and dimension columns, in the order the spec's Dashboard list
 * view shows them (p. 35). Keys are the engine's, so adding a column here
 * needs no other change than a header translation.
 */
const INTELLIGENCE_COLUMNS = [
  { key: KPI.projectHealthScore, labelKey: 'health' },
  { key: KPI.readinessScore, labelKey: 'readiness' },
  { key: KPI.executionScore, labelKey: 'execution' },
  { key: KPI.qualityScore, labelKey: 'qa' },
  { key: KPI.dependencyScore, labelKey: 'dependencies' },
] as const;

interface ProjectListProps {
  projects: ProjectListProject[];
  itemsPerPage?: number;
  viewType?: 'list' | 'card';
  onProjectClick?: (project: ProjectListProject) => void;
  /** The project's URL, so cards and rows are real links (open in new tab). */
  projectHref?: (project: ProjectListProject) => string;
  onProjectDelete?: (projectId: string) => void;
  onProjectTemplateCreate?: (project: ProjectListProject) => void;
}


// project list component for dashboard page set current page, selected items, select all, and items per page with state management
const ProjectList: React.FC<ProjectListProps> = ({
                                                   projects,
                                                   itemsPerPage = 8,
                                                   viewType = 'list',
                                                   onProjectClick,
                                                   projectHref,
                                                   onProjectDelete,
                                                   onProjectTemplateCreate
                                                 }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const { t } = useTranslation('project');

  const getStatusBadgeClass = (progress: number) => {
    if (progress === 0) {
      return styles.statusPlaceholder;
    } else if (progress === 100) {
      return styles.statusCompleted;
    } else {
      return styles.statusInProgress;
    }
  };

  const getStatusText = (progress: number): string => {
    if (progress === 0) {
      return t('list.status.planned');
    } else if (progress === 100) {
      return t('list.status.done');
    } else {
      return t('list.status.inProgress');
    }
  };

  const handleDeleteClick = (projectId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setProjectToDelete(projectId);
    setIsDeleteDialogOpen(true);
  };

  const handleTemplateClick = (project: ProjectListProject, event: React.MouseEvent) => {
    event.stopPropagation();
    onProjectTemplateCreate?.(project);
  };

  const handleConfirmDelete = () => {
    if (projectToDelete && onProjectDelete) {
      onProjectDelete(projectToDelete);
    }
    setProjectToDelete(null);
    setIsDeleteDialogOpen(false);
  };

  // Pagination logic
  let paginatedProjects: ProjectListProps['projects'] = [];
  let totalPages = 1;
  const cardsPerPage = viewType === 'card' ? 9 : itemsPerPage;
  
  totalPages = Math.ceil(projects.length / cardsPerPage);
  const startIndex = (currentPage - 1) * cardsPerPage;
  paginatedProjects = projects.slice(startIndex, startIndex + cardsPerPage);

  if (viewType === 'card') {
    return (
      <div className={styles.cardViewContainer}>
        <div className={styles.cardGrid}>
          {paginatedProjects.map((project) => (
            <ProjectCardView 
              key={project.id}
              project={project} 
              onDelete={handleDeleteClick}
              onTemplateCreate={handleTemplateClick}
              onClick={() => onProjectClick?.(project)}
              href={projectHref?.(project)}
            />
          ))}
        </div>
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            {t('list.pagination.previous')}
          </button>
          <div className={styles.paginationInfo}>
            {t('list.pagination.page', { current: currentPage, total: totalPages })}
          </div>
          <button
            className={styles.paginationButton}
            onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            {t('list.pagination.next')}
          </button>
        </div>
        <ConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          title={t('list.deleteDialog.title')}
          message={t('list.deleteDialog.message')}
          confirmText={t('list.deleteDialog.confirm')}
          cancelText={t('list.deleteDialog.cancel')}
          type="danger"
        />
      </div>
    );
  }

  const tableHeight = 44 + (paginatedProjects.length * 72);

  return (
    <div className={styles.tableContainer}>
      <div className={styles.table} style={{ height: `${tableHeight}px` }}>
        <div className={styles.column}>
          <div className={styles.headerCell}>
            <div className={styles.headerTitle}>
              <span>{t('list.headers.project')}</span>
            </div>
          </div>
          {paginatedProjects.map((project) => (
            <a
              key={project.id}
              href={projectHref?.(project)}
              className={styles.cell}
              onClick={inAppClick(() => onProjectClick?.(project))}
              style={{ cursor: 'pointer' }}
            >
              <img src={project.logoUrl} alt={project.title} className={styles.projectLogo} />
              <div className={styles.projectInfo}>
                <span className={styles.projectTitle}>{project.title}</span>
                <div className={`${styles.badge} ${getStatusBadgeClass(project.progress)}`}>
                  {getStatusText(project.progress)}
                </div>
              </div>
            </a>
          ))}
        </div>

        {/*
          * The Project Intelligence columns of the spec's Dashboard mockup
          * (p. 35). Each reads the project's stored KPI value; a KPI that was
          * not computable has no value and shows a dash, never a 0.
          */}
        {INTELLIGENCE_COLUMNS.map((column) => (
          <div key={column.key} className={styles.column}>
            <div className={styles.headerCell}>
              <span>{t(`list.headers.${column.labelKey}`)}</span>
            </div>
            {paginatedProjects.map((project) => (
              <div key={project.id} className={styles.cell}>
                <ProjectScoreCell value={project.intelligence?.scores.get(column.key)} />
              </div>
            ))}
          </div>
        ))}

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.progress')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={styles.cell}>
              <div className={styles.progressBar}>
                <div className={styles.progressTrack}>
                  <div 
                    className={`${styles.progressFill} ${
                      project.progress === 100 ? styles.progressFull :
                      project.progress >= 50 ? styles.progressMedium :
                      styles.progressLow
                    }`}
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
                <span className={styles.progressText}>{project.progress}%</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.trend')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={styles.cell}>
              <ProjectTrendCell value={project.intelligence?.healthTrend} />
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.actionPoints')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={styles.cell}>
              <span className={styles.actionPointCount}>
                {project.intelligence ? project.intelligence.openAlerts : '—'}
              </span>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <span>{t('list.headers.actions')}</span>
          </div>
          {paginatedProjects.map((project) => (
            <div key={project.id} className={`${styles.cell} ${styles.actionsCell}`}>
              <div className={styles.actionButtonGroup}>
                {onProjectTemplateCreate && (
                    <button
                        className={styles.templateButton}
                        onClick={(e) => handleTemplateClick(project, e)}
                        title={t('list.actions.createTemplate')}
                    >
                      <IconTemplate size={18}/>
                    </button>
                )}
                <button
                    className={styles.deleteButton}
                    onClick={(e) => handleDeleteClick(project.id, e)}
                    title={t('list.deleteDialog.confirm')}
                >
                  <IconTrash size={18}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.pagination}>
        <button 
          className={styles.paginationButton} 
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          {t('list.pagination.previous')}
        </button>
        <div className={styles.paginationInfo}>
          {t('list.pagination.page', { current: currentPage, total: totalPages })}
        </div>
        <button 
          className={styles.paginationButton}
          onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          {t('list.pagination.next')}
        </button>
      </div>
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('list.deleteDialog.title')}
        message={t('list.deleteDialog.message')}
        confirmText={t('list.deleteDialog.confirm')}
        cancelText={t('list.deleteDialog.cancel')}
        type="danger"
      />
    </div>
  );
};

export default ProjectList; 
